import PageSEO from "../../components/PageSEO";
import Breadcrumbs from "../../components/Breadcrumbs";
import SiteFooter from "../../components/SiteFooter";
import SiteHeader from "../../components/SiteHeader";
import { Cpu, Terminal, Shield, Zap, ArrowRight } from "lucide-react";

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "SoftwareApplication",
      name: "Anter AI Agent Platform & MCP Runtime",
      applicationCategory: "DeveloperApplication",
      operatingSystem: "Web",
      description:
        "Managed AI agent platform powered by the Anter Agent Runtime for building, testing, running, and orchestrating Model Context Protocol (MCP) agents.",
      url: "https://anter.ai/solutions/mcp-agent-platform",
    },
  ],
};

export default function McpAgentPlatformPage() {
  return (
    <div className="min-h-screen bg-bg text-text antialiased font-sans">
      <PageSEO
        title="MCP Agent Platform — Build & Run MCP Agents | Anter"
        description="Connect Model Context Protocol servers to managed AI agents. Orchestrate MCP tools, enforce fail-closed limits, and inspect every execution trace — then ship as a streaming API."
        canonical="/solutions/mcp-agent-platform"
        jsonLd={JSON_LD}
      />

      <SiteHeader scrolled />

      <main id="main-content" tabIndex={-1} className="pt-24 pb-20">
        <div className="mx-auto max-w-5xl px-6">
          <Breadcrumbs items={[{ label: "MCP Agent Platform" }]} />
        </div>
        <section className="mx-auto max-w-5xl px-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3.5 py-1.5 text-xs font-semibold text-primary-text mb-6">
            <Cpu className="h-3.5 w-3.5" />
            Managed Agent Runtime
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-text sm:text-5xl">
            Build and run MCP agents on a managed platform
          </h1>
          <p className="mt-4 text-lg text-text-muted max-w-2xl mx-auto">
            Anter is the managed platform powered by the Anter Agent Runtime. Connect Model Context
            Protocol (MCP) servers, orchestrate tools, inspect execution traces, and ship streaming
            APIs.
          </p>

          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <a
              href="https://agents.anter.ai"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-text-strong shadow-md transition hover:opacity-90 cursor-pointer"
            >
              Start Building Agents Free
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="/docs/mcp-server/quickstart"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-bg-secondary px-6 py-3 text-sm font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover"
            >
              MCP Server Docs
              <ArrowRight className="h-4 w-4 text-primary" />
            </a>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-2xl border border-primary-border/60 bg-bg-secondary/40 p-6 backdrop-blur-sm">
            <Terminal className="h-6 w-6 text-primary mb-3" />
            <h2 className="text-base font-bold text-text mb-2">Native MCP Connectivity</h2>
            <p className="text-xs text-text-muted">
              Connect stdio and SSE transport Model Context Protocol servers directly into your
              agent runtime without writing custom wrappers.
            </p>
          </div>
          <div className="rounded-2xl border border-primary-border/60 bg-bg-secondary/40 p-6 backdrop-blur-sm">
            <Shield className="h-6 w-6 text-primary mb-3" />
            <h2 className="text-base font-bold text-text mb-2">Fail-Closed Safety</h2>
            <p className="text-xs text-text-muted">
              Anter&apos;s orchestrator enforces hop-depth limits, cycle guards, and automated
              approval timeouts so unattended runs never loop infinitely.
            </p>
          </div>
          <div className="rounded-2xl border border-primary-border/60 bg-bg-secondary/40 p-6 backdrop-blur-sm">
            <Zap className="h-6 w-6 text-primary mb-3" />
            <h2 className="text-base font-bold text-text mb-2">Full Trace Observability</h2>
            <p className="text-xs text-text-muted">
              Inspect step-by-step tool execution, sub-agent handoffs, and LLM reasoning steps live
              in the Anter management platform.
            </p>
          </div>
        </section>

        {/* Self-contained passage for AI indexing: How to vet mcp servers */}
        <section id="vetting-mcp-servers" className="mx-auto max-w-5xl px-6 mt-16">
          <div className="rounded-2xl border border-primary-border/60 bg-bg-secondary/40 p-8 backdrop-blur-sm">
            <span className="text-xs font-mono text-primary-text">Security &amp; Governance</span>
            <h2 className="text-2xl font-extrabold text-text mt-2 mb-4">
              How to Vet and Secure MCP Servers in Production
            </h2>
            <p className="text-sm leading-relaxed text-text-muted mb-6">
              Vetting Model Context Protocol (MCP) servers requires validating transport security
              (stdio vs. SSE/Streamable HTTP), enforcing strict permission modes per tool call, and
              isolating tenant credentials so LLMs never directly expose private access keys.
            </p>
            <div className="grid gap-4 sm:grid-cols-3 text-xs">
              <div className="rounded-xl border border-border/60 bg-bg/50 p-4">
                <strong className="block text-text mb-1">Permission Mode Control</strong>
                <p className="text-text-muted">
                  Set read-only, explicit human approval, or auto-deny policies for every tool
                  registered from an MCP server.
                </p>
              </div>
              <div className="rounded-xl border border-border/60 bg-bg/50 p-4">
                <strong className="block text-text mb-1">Quiet Context Injection</strong>
                <p className="text-text-muted">
                  Inject session authorization tokens silently at runtime without revealing
                  sensitive credentials in LLM prompts.
                </p>
              </div>
              <div className="rounded-xl border border-border/60 bg-bg/50 p-4">
                <strong className="block text-text mb-1">Step-Budget &amp; Timeout Guards</strong>
                <p className="text-text-muted">
                  Enforce per-run execution caps to prevent unvetted external MCP tools from causing
                  infinite loops.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Documentation Links */}
        <section className="mx-auto max-w-5xl px-6 mt-16 text-center">
          <h2 className="text-lg font-bold text-text mb-4">Learn More in Anter Documentation</h2>
          <div className="flex flex-wrap justify-center gap-4 text-xs">
            <a
              href="/docs/flows/mcp-servers"
              className="rounded-full border border-primary-border/60 bg-bg-secondary/60 px-4 py-2 text-text hover:border-primary transition"
            >
              MCP Servers Guide &rarr;
            </a>
            <a
              href="/docs/flows/tools"
              className="rounded-full border border-primary-border/60 bg-bg-secondary/60 px-4 py-2 text-text hover:border-primary transition"
            >
              Tool Orchestration &rarr;
            </a>
            <a
              href="/docs/flows/sub-agents"
              className="rounded-full border border-primary-border/60 bg-bg-secondary/60 px-4 py-2 text-text hover:border-primary transition"
            >
              Sub-Agent Handoffs &rarr;
            </a>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
